// Wybór rozmiaru na karcie produktu — dwie formy tej samej kontrolki.
//
// Wskaźnik (myszka): własna lista rozwijana. Natywny <select> rysuje system operacyjny —
// na macOS wychodzi ciemny panel z granatowym podświetleniem, który nie ma nic wspólnego
// z resztą sklepu.
//
// Dotyk: dolny arkusz z kaflami rozmiarów (prośba klienta 2026-09-08, wzór z Legii).
// Systemowy picker iOS pokazuje rozmiary jako obracany walec — widać 3 pozycje naraz,
// nie widać, których rozmiarów już nie ma, a przy jednej sztuce na rozmiar to jest
// najważniejsza informacja na tym ekranie.
//
// Select zostaje w DOM jako źródło prawdy (nazwa pola, wartość, wysyłka formularza) —
// własna warstwa tylko go obsługuje i emituje `change`, więc pdp-variations.js nic nie wie
// o tej zamianie. Bez JS zostaje natywny select, więc karta działa też wtedy.

// Pole, które zastępuje natywny select. Wygląd 1:1 z .kb-attr-select, żeby stan spoczynku
// był ten sam niezależnie od tego, co otwiera się po kliknięciu (lista albo arkusz).
function zbudujTrigger(select, rodzajPopupu) {
  const wrap = document.createElement('div');
  wrap.className = 'kb-select';

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'kb-select__trigger';
  trigger.setAttribute('aria-haspopup', rodzajPopupu);
  trigger.setAttribute('aria-expanded', 'false');

  const etykieta = document.createElement('span');
  etykieta.className = 'kb-select__value';
  trigger.appendChild(etykieta);
  wrap.appendChild(trigger);

  select.parentNode.insertBefore(wrap, select);
  select.classList.add('kb-select__native');
  return { wrap, trigger, etykieta };
}

// Które rozmiary da się kupić — z `data-product_variations` formularza (te same dane, na
// których pdp-variations.js włącza „Dodaj do koszyka").
//
// Uwaga na pułapkę: WooCommerce NIE wstawia do tego JSON-a wariantów bez stanu, ale rozmiar
// zostaje na liście atrybutów. Wyprzedany rozmiar był więc do wybrania, po czym „Dodaj do
// koszyka" po cichu zostawał wyszarzony — dokładnie ten scenariusz zgłaszał klient sklepu.
// Dlatego: rozmiar nieobecny w JSON-ie = sprzedany. Wyjątek, gdy któryś wariant pasuje do
// dowolnego rozmiaru (puste `attribute_*`) albo gdy danych nie ma wcale — wtedy nic nie gasimy.
function mapaDostepnosci(select) {
  const form = select.closest('form.variations_form');
  const raw = form && form.getAttribute('data-product_variations');
  if (!raw) return () => true;
  let warianty;
  try { warianty = JSON.parse(raw); } catch (e) { return () => true; }
  const klucz = 'attribute_' + String(select.name || '').replace(/^attribute_/, '');
  const stan = new Map();
  let dowolnyRozmiar = false;
  warianty.forEach((v) => {
    const mozna = !!(v.is_in_stock && v.is_purchasable);
    const wartosc = String((v.attributes && v.attributes[klucz]) || '').toLowerCase();
    if (!wartosc) { dowolnyRozmiar = dowolnyRozmiar || mozna; return; }
    stan.set(wartosc, stan.get(wartosc) === true || mozna);
  });
  if (dowolnyRozmiar || stan.size === 0) return () => true;
  return (v) => stan.get(String(v).toLowerCase()) === true;
}

function zbudujListe(select) {
  const { wrap, trigger, etykieta } = zbudujTrigger(select, 'listbox');

  const panel = document.createElement('div');
  panel.className = 'kb-select__panel';
  panel.setAttribute('role', 'listbox');
  panel.hidden = true;

  // Placeholder („Wybierz rozmiar") zostaje w natywnym selekcie jako wartość pusta, ale NIE
  // trafia na listę — to podpowiedź, nie rozmiar do kliknięcia. Trigger i tak go pokazuje,
  // dopóki nic nie wybrano. Trzymamy oryginalny indeks, bo lista jest krótsza niż select.
  const dostepny = mapaDostepnosci(select);
  const opcje = [];
  Array.from(select.options).forEach((o, i) => {
    if (o.value === '') return;
    const el = document.createElement('div');
    el.className = 'kb-select__option';
    el.setAttribute('role', 'option');
    el.setAttribute('tabindex', '-1');
    el.dataset.value = o.value;
    el.dataset.idx = String(i);
    // Sama nazwa rozmiaru w spanie: przekreślenie ma dotyczyć rozmiaru, nie całego wiersza
    // (text-decoration z rodzica dziedziczy się i dziecko już go nie zdejmie).
    const wartosc = document.createElement('span');
    wartosc.className = 'kb-select__val';
    wartosc.textContent = o.text;
    el.appendChild(wartosc);
    if (dostepny(o.value)) {
      el.addEventListener('click', () => wybierz(opcje.indexOf(el)));
    } else {
      // Rozmiar sprzedany zostaje widoczny (informacja „ten rozmiar był"), ale nie da się go wybrać.
      el.classList.add('is-out');
      el.setAttribute('aria-disabled', 'true');
      const brak = document.createElement('span');
      brak.className = 'kb-select__out';
      brak.textContent = 'Sprzedany';
      el.appendChild(brak);
    }
    // Najechanie PRZESUWA podświetlenie, nie dokłada drugiego. Bez tego kursor i klawiatura
    // podświetlały dwa różne wiersze naraz.
    el.addEventListener('mouseenter', () => podswietl(opcje.indexOf(el)));
    panel.appendChild(el);
    opcje.push(el);
  });

  wrap.appendChild(panel);

  let otwarty = false;
  let aktywny = Math.max(0, select.selectedIndex);

  function odswiezEtykiete() {
    const o = select.options[select.selectedIndex];
    etykieta.textContent = o ? o.text : '';
    trigger.classList.toggle('is-placeholder', !select.value);
    opcje.forEach((el) => {
      const wybrany = Number(el.dataset.idx) === select.selectedIndex && select.value !== '';
      el.classList.toggle('is-selected', wybrany);
      el.setAttribute('aria-selected', wybrany ? 'true' : 'false');
    });
  }

  function podswietl(i) {
    aktywny = Math.min(Math.max(i, 0), opcje.length - 1);
    opcje.forEach((el, n) => el.classList.toggle('is-active', n === aktywny));
    opcje[aktywny]?.scrollIntoView({ block: 'nearest' });
  }

  function otworz() {
    if (otwarty) return;
    otwarty = true;
    panel.hidden = false;
    // reflow, żeby przejście wystartowało ze stanu zamkniętego, a nie od razu w docelowym
    void panel.offsetWidth;
    wrap.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    // Podświetlamy aktualnie wybrany rozmiar, a przy braku wyboru — pierwszy z listy.
    const biezacy = opcje.findIndex((el) => Number(el.dataset.idx) === select.selectedIndex);
    podswietl(biezacy >= 0 ? biezacy : 0);
  }

  function zamknij({ oddajFokus = false } = {}) {
    if (!otwarty) return;
    otwarty = false;
    wrap.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    const schowaj = () => { if (!otwarty) panel.hidden = true; };
    panel.addEventListener('transitionend', schowaj, { once: true });
    setTimeout(schowaj, 260); // gdy transition nie wystartuje (reduced motion)
    if (oddajFokus) trigger.focus();
  }

  function wybierz(i) {
    const el = opcje[i];
    if (!el || el.classList.contains('is-out')) return;
    select.selectedIndex = Number(el.dataset.idx);
    select.dispatchEvent(new Event('change', { bubbles: true }));
    odswiezEtykiete();
    zamknij({ oddajFokus: true });
  }

  trigger.addEventListener('click', () => (otwarty ? zamknij() : otworz()));

  trigger.addEventListener('keydown', (e) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
      e.preventDefault();
      otworz();
    }
  });

  wrap.addEventListener('keydown', (e) => {
    if (!otwarty) return;
    if (e.key === 'Escape') { e.preventDefault(); zamknij({ oddajFokus: true }); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); podswietl(aktywny + 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); podswietl(aktywny - 1); }
    else if (e.key === 'Home') { e.preventDefault(); podswietl(0); }
    else if (e.key === 'End') { e.preventDefault(); podswietl(opcje.length - 1); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); wybierz(aktywny); }
  });

  document.addEventListener('click', (e) => { if (!wrap.contains(e.target)) zamknij(); });
  // Zmiana z zewnątrz (np. reset formularza) ma się odbić na etykiecie.
  select.addEventListener('change', odswiezEtykiete);

  odswiezEtykiete();
}

// Dolny arkusz z kaflami rozmiarów (dotyk).
function zbudujArkusz(select) {
  const { trigger, etykieta } = zbudujTrigger(select, 'dialog');
  const dostepny = mapaDostepnosci(select);

  const arkusz = document.createElement('div');
  arkusz.className = 'kb-size-sheet';
  arkusz.hidden = true;

  const tlo = document.createElement('div');
  tlo.className = 'kb-size-sheet__backdrop';

  const panel = document.createElement('div');
  panel.className = 'kb-size-sheet__panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');

  const naglowek = document.createElement('div');
  naglowek.className = 'kb-size-sheet__head';
  const tytul = document.createElement('span');
  tytul.className = 'kb-size-sheet__title';
  // Nazwa atrybutu z ukrytej etykiety pola — żeby nie zaszywać słowa „Rozmiar" w JS.
  const etykietaPola = select.closest('[data-attr-row]')?.querySelector('label');
  tytul.textContent = (etykietaPola && etykietaPola.textContent.trim()) || 'Rozmiar';
  panel.setAttribute('aria-label', tytul.textContent);

  const zamknijBtn = document.createElement('button');
  zamknijBtn.type = 'button';
  zamknijBtn.className = 'kb-size-sheet__x';
  zamknijBtn.setAttribute('aria-label', 'Zamknij');
  zamknijBtn.innerHTML = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  naglowek.append(tytul, zamknijBtn);

  const siatka = document.createElement('div');
  siatka.className = 'kb-size-sheet__grid';
  siatka.setAttribute('role', 'listbox');
  siatka.setAttribute('aria-label', tytul.textContent);

  const kafle = [];
  Array.from(select.options).forEach((o, i) => {
    if (o.value === '') return; // „Wybierz rozmiar" to podpowiedź, nie rozmiar do kliknięcia
    const kafel = document.createElement('button');
    kafel.type = 'button';
    kafel.className = 'kb-size-sheet__opt';
    kafel.setAttribute('role', 'option');
    kafel.dataset.idx = String(i);

    const wartosc = document.createElement('span');
    wartosc.className = 'kb-size-sheet__val';
    wartosc.textContent = o.text;
    kafel.appendChild(wartosc);

    if (dostepny(o.value)) {
      kafel.addEventListener('click', () => wybierz(kafel));
    } else {
      kafel.classList.add('is-out');
      kafel.disabled = true;
      const brak = document.createElement('span');
      brak.className = 'kb-size-sheet__out';
      brak.textContent = 'Sprzedany';
      kafel.appendChild(brak);
    }

    siatka.appendChild(kafel);
    kafle.push(kafel);
  });

  panel.append(naglowek, siatka);
  arkusz.append(tlo, panel);
  document.body.appendChild(arkusz);

  let otwarty = false;

  function odswiezEtykiete() {
    const o = select.options[select.selectedIndex];
    etykieta.textContent = o ? o.text : '';
    trigger.classList.toggle('is-placeholder', !select.value);
    kafle.forEach((k) => {
      const wybrany = Number(k.dataset.idx) === select.selectedIndex && select.value !== '';
      k.classList.toggle('is-selected', wybrany);
      k.setAttribute('aria-selected', wybrany ? 'true' : 'false');
    });
  }

  function otworz() {
    if (otwarty) return;
    otwarty = true;
    arkusz.hidden = false;
    void arkusz.offsetWidth; // reflow, żeby panel wjechał, a nie pojawił się gotowy
    arkusz.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden'; // ten sam sposób co szuflada koszyka
    (kafle.find((k) => k.classList.contains('is-selected')) || kafle.find((k) => !k.disabled))
      ?.focus({ preventScroll: true });
  }

  function zamknij({ oddajFokus = false } = {}) {
    if (!otwarty) return;
    otwarty = false;
    arkusz.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    const schowaj = () => { if (!otwarty) arkusz.hidden = true; };
    panel.addEventListener('transitionend', schowaj, { once: true });
    setTimeout(schowaj, 400); // gdy przejście nie wystartuje (reduced motion)
    if (oddajFokus) trigger.focus({ preventScroll: true });
  }

  function wybierz(kafel) {
    select.selectedIndex = Number(kafel.dataset.idx);
    select.dispatchEvent(new Event('change', { bubbles: true }));
    odswiezEtykiete();
    // Krótka pauza z zapalonym kaflem — bez niej wybór znika, zanim kciuk zdąży go zobaczyć.
    setTimeout(() => zamknij({ oddajFokus: true }), 140);
  }

  trigger.addEventListener('click', () => (otwarty ? zamknij() : otworz()));
  tlo.addEventListener('click', () => zamknij());
  zamknijBtn.addEventListener('click', () => zamknij({ oddajFokus: true }));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && otwarty) { e.stopPropagation(); zamknij({ oddajFokus: true }); }
  }, true);
  select.addEventListener('change', odswiezEtykiete);

  odswiezEtykiete();
}

function initPDPSizeSelect() {
  // Wskaźnik precyzyjny dostaje listę rozwijaną, dotyk — dolny arkusz z kaflami.
  const wskaznik = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  document.querySelectorAll('select[data-attr-select]').forEach((sel) => {
    if (sel.dataset.kbEnhanced) return; // już podmieniony — nie dublujemy kontrolki
    sel.dataset.kbEnhanced = '1';
    if (wskaznik) zbudujListe(sel);
    else zbudujArkusz(sel);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState !== 'loading') initPDPSizeSelect();
  else document.addEventListener('DOMContentLoaded', initPDPSizeSelect);
  // Druga próba po pełnym załadowaniu i po powrocie z bfcache. Gdy pierwsza nie zdąży
  // (wolny skrypt, dosunięta treść), kontrolka po cichu zjeżdża do natywnej listy systemu —
  // a to wygląda jak zepsuta strona, nie jak brak ulepszenia.
  window.addEventListener('load', initPDPSizeSelect);
  window.addEventListener('pageshow', initPDPSizeSelect);
}
